/**
 * -----------------------------------------------------------------------------
 * Popup - Add Widget
 * -----------------------------------------------------------------------------
 * This popup is used for adding a new widget to the dashboard.
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { serializeErrorKeyValues } from "../../api/network/errorCodes";
import { useUpdateDashboardMutation } from "../../api/network/overviewApiServices";
import { useGetReportsByCatTypeQuery } from "../../api/network/reportApiServices";
import {
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../api/store/commonSlice";
import {
  ADD_CUSTOM_WIDGETS,
  CustomWidget,
  DASHBOARD_WIDGET_CATEGORIES,
  DASHBOARD_WIDGET_GRAPH_TYPE,
  DASHBOARD_WIDGET_TYPE,
  DashboardWidget,
} from "../../api/types/Overview";
import { TReport } from "../../api/types/Report";
import widgetScreenshots, {
  CustomWidgetScreenshots,
} from "../../assets/images/widget-screenshots/widgetScreenshots";
import svgIcons from "../../assets/svg/svgIcons";
import AccordianControlled from "../../components/accordianControlled";
import AccordianWithOptions from "../../components/accordianWithOptions";
import { AdminFormFieldSubmit } from "../../components/admin/formFields";
import { APP_CONFIG, REPORT_TYPES } from "../../constants/constants";
import { routeUrls } from "../../navigation/routeUrls";
import { useOverviewContext } from "./context/overview.context";
import { EWidgetChartTypes } from "./types/overview.types";
import { getChartTypeNameById } from "./utils/chart";

const WidgetSelectCustomReportButton = ({
  subReport,
  onClick,
}: {
  subReport: TReport;
  onClick: () => void;
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "overviewScreen.add_widgets",
  });
  return (
    <button
      className={`px-4 py-3 text-left text-sm font-semibold rounded-lg ${subReport.is_group_by_aggregate ? "text-sidemenu-text hover:bg-accent-blue-paleO66" : "text-sidemenu-text/25 cursor-not-allowed"}`}
      onClick={
        subReport.is_group_by_aggregate
          ? onClick
          : () => {
              toast.error(t("custom.cannot_select_report"));
            }
      }
      // disabled={!subReport.is_group_by_aggregate}
    >
      {subReport.name}
    </button>
  );
};

const ModalDashboardAddWidget = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "overviewScreen.add_widgets",
  });
  const { t: tWidgetTitles } = useTranslation("translation", {
    keyPrefix: "overviewScreen.widget_titles",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openReport, setOpenReport] = useState<number>(0);

  const {
    isFetchingDashboardList,
    refetchDashboardList,
    dashList,
    selectedDashboard,
    dashboardColumns,
    isLoadingUpdateDashboard,
    isFetchingCustomList,
    refetchCustomWidgetList,
    customList,
    setWidgetMarkedForDeletion,
  } = useOverviewContext();

  // dashboard mutation
  const [
    updateDashboardAPITrigger,
    { isLoading: isLoadingUpdateDashboard_thisComponent },
  ] = useUpdateDashboardMutation();

  const payloadCustom = {
    type: REPORT_TYPES.CUSTOM,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
  };
  const payloadShared = {
    type: REPORT_TYPES.SHARED_WITH_ME,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
  };

  const { data: dataReportsByCategoryCustom } = useGetReportsByCatTypeQuery(
    payloadCustom,
    { refetchOnMountOrArgChange: true }
  );
  const { data: dataReportsByCategoryShared } = useGetReportsByCatTypeQuery(
    payloadShared,
    { refetchOnMountOrArgChange: true }
  );

  const hideModal = () => {
    dispatch(
      setModalsData({
        ...modalsState,
        showDashboardAddWidget: false,
        editDashboardId: null,
      })
    );
  };

  const handleAddWidget = (widgetType: string) => {
    if (!selectedDashboard) {
      toast.error(t("toast.dashboard_not_selected"));
      return;
    }
    const thisDashboard = dashList?.find((d) => d.id === selectedDashboard);

    // find column with least widgets
    const columns = [1, 2, 3];
    const columnCounts = columns.map(
      (col) =>
        thisDashboard?.widgets?.filter((w) => w.page_column === col).length || 0
    );
    const leastWidgetsColumnIndex = columnCounts.indexOf(
      Math.min(...columnCounts)
    );
    const leastWidgetsColumn = columns[leastWidgetsColumnIndex];
    console.log("leastWidgetsColumn", leastWidgetsColumn);

    updateDashboardAPITrigger({
      id: selectedDashboard,
      widgets: [
        ...(thisDashboard?.widgets || []),
        {
          // name: DASHBOARD_WIDGET_TYPE[widgetType as keyof typeof DASHBOARD_WIDGET_TYPE] as DASHBOARD_WIDGET_TYPE,
          name: widgetType as any as DASHBOARD_WIDGET_TYPE,
          page_column: leastWidgetsColumn,
          type: DASHBOARD_WIDGET_GRAPH_TYPE.PIE_CHART,
        } as DashboardWidget,
      ],
    })
      .unwrap()
      .then((data) => {
        toast.success(t("toast.add_success"), {
          autoClose: 2000,
          position: "top-center",
        });
        refetchDashboardList();
        // hideModal(); don't close the modal
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.add_failed")];
        toast.error(errors?.join(" "));
      })
      .finally(() => {
        // setSubmitting(false);
      });
  };

  const handleAddCustomWidget = (widget: CustomWidget) => {
    if (!selectedDashboard) {
      toast.error(t("toast.dashboard_not_selected"));
      return;
    }
    const thisDashboard = dashList?.find((d) => d.id === selectedDashboard);

    // find column with least widgets
    const columns = [1, 2, 3];
    const columnCounts = columns.map(
      (col) =>
        thisDashboard?.widgets?.filter((w) => w.page_column === col).length || 0
    );
    const leastWidgetsColumnIndex = columnCounts.indexOf(
      Math.min(...columnCounts)
    );
    const leastWidgetsColumn = columns[leastWidgetsColumnIndex];
    console.log("leastWidgetsColumn", leastWidgetsColumn);

    updateDashboardAPITrigger({
      id: selectedDashboard,
      widgets: [
        ...(thisDashboard?.widgets || []),
        {
          page_column: leastWidgetsColumn,
          type: widget.graph_type,
          category: 2,
          custom_widget: widget.id,
          custom_widget_title: widget.title,
        } as unknown as DashboardWidget,
      ],
    })
      .unwrap()
      .then((data) => {
        toast.success(t("toast.add_success"), {
          autoClose: 2000,
          position: "top-center",
        });
        refetchDashboardList();
        // hideModal(); don't close the modal
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.add_failed")];
        toast.error(errors?.join(" "));
      })
      .finally(() => {
        // setSubmitting(false);
      });
  };

  const deleteCustomWidgetButtonClickCallback = (widget: CustomWidget) => {
    setWidgetMarkedForDeletion(widget);
  };

  const [openAccordian, setOpenAccordian] = useState<number>(0);

  if (!modalsState.showDashboardAddWidget) return null;

  const widgetsOnDashboard = dashboardColumns.col1.colWidgets
    .concat(dashboardColumns.col2.colWidgets)
    .concat(dashboardColumns.col3.colWidgets);

  const availableWidgets = Object.entries(DASHBOARD_WIDGET_TYPE)
    .filter(([_, widgetTypeSlug]) => isNaN(parseInt(widgetTypeSlug as string)))
    // .filter(([widgetType, _]) => !dashList?.find(d => d.id === selectedDashboard)?.widgets?.find(w => w.name.toString() === widgetType))
    .filter(
      ([widgetTypeId, _]) =>
        !widgetsOnDashboard.find((w) => w?.name?.toString() === widgetTypeId)
    );

  // Find custom widgets which are not available in available widget list
  const availableCustomWidgets = customList?.filter((custom) => {
    return !widgetsOnDashboard.find(
      (w) => w?.custom_widget?.toString() === custom.id.toString()
    );
  });

  const getWidgetImage = (type: EWidgetChartTypes): string => {
    return (CustomWidgetScreenshots as Record<string, string>)[
      `custom_${type.toLowerCase()}`
    ];
  };

  const onReportClick = (report: TReport) => {
    dispatch(
      setModalsData({
        ...modalsState,
        showDashboardAddWidget: false,
        showUseCustomReport: true,
        customWidgetReport: report,
      })
    );
  };

  const onCreateCustomReportClick = () => {
    dispatch(
      setModalsData({
        ...modalsState,
        showDashboardAddWidget: false,
      })
    );
    navigate(routeUrls.dashboardChildren.reports);
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-modal-overlay z-dashboard-widget-overlay"
        onClick={hideModal}
      ></div>
      <div className="absolute top-0 mt-0 right-0 bg-white rounded-lg flex flex-col px-3 py-3 w-64 shadow-lg border border-black/10 z-dashboard-widget">
        {/* Header */}
        <div className="flex flex-row items-center justify-between mb-4">
          <label className="text-xl font-semibold text-heading-black">
            {t("heading")}
          </label>
          <button
            type="button"
            className="flex justify-end p-2"
            onClick={hideModal}
          >
            {isLoadingUpdateDashboard ||
            isLoadingUpdateDashboard_thisComponent ? (
              <img
                src={svgIcons.LoadingAnimation}
                alt="loading"
                className="size-6"
              />
            ) : (
              <img src={svgIcons.CloseIcon} alt="close" className="size-6" />
            )}
          </button>
        </div>

        {/* Content */}
        <div className="text-lg font-bold text-gray-500 mb-4">
          {t("section_standard")}
        </div>
        <div className="flex flex-col gap-2 max-h-[calc(100vh-190px)] overflow-auto">
          {Object.keys(DASHBOARD_WIDGET_CATEGORIES).map((categorySlug, i) => {
            const categoryWidgets = availableWidgets.filter(
              ([widgetTypeId, _]) =>
                DASHBOARD_WIDGET_CATEGORIES[
                  categorySlug as keyof typeof DASHBOARD_WIDGET_CATEGORIES
                ].includes(
                  parseInt(`${widgetTypeId as any as DASHBOARD_WIDGET_TYPE}`)
                )
            );
            return (
              <AccordianControlled
                title={t(`category_${categorySlug}`)}
                hasDetailsPage={false}
                buttonSpacingClass=""
                isOpen={openAccordian === i}
                onClick={() => {
                  setOpenAccordian(openAccordian === i ? -1 : i);
                }}
                wrapperClass={
                  openAccordian === i
                    ? `${categoryWidgets.length === 0 ? "" : "flex-grow h-[calc(100vh-280px)]"} flex flex-col`
                    : ""
                }
                contentClass="flex-grow relative"
                handleClass="bg-accent-blue-paleO66 ps-4 pe-2 rounded-lg"
                key={i}
              >
                <div
                  className={`${categoryWidgets.length === 0 ? "" : "absolute"} w-full h-full flex flex-col gap-4 py-4 overflow-y-auto`}
                >
                  {categoryWidgets.length === 0 ? (
                    <p className="flex-grow flex items-center text-heading-gray888 opacity-70 text-sm px-10 py-4 text-center">
                      {t("no_widgets")}
                    </p>
                  ) : (
                    categoryWidgets.map(([widgetType, _], index) => (
                      <React.Fragment key={index}>
                        <h4 className="flex justify-between items-start gap-2 font-semibold">
                          {/* Widget Title */}
                          <span
                            className="flex-grow min-h-9 flex items-center Xleading-4 Xoverflow-hidden Xwhitespace-nowrap Xtext-ellipsis"
                            title={tWidgetTitles(`enum_${widgetType}`)}
                          >
                            <span className="text-sm leading-4 line-clamp-3">
                              {tWidgetTitles(`enum_${widgetType}`)}
                            </span>
                          </span>

                          {/* Widget Actions */}
                          <span
                            className={`flex${modalsState.showDashboardAddWidget ? " flex-row-reverse" : ""} gap-2 shrink-0`}
                          >
                            {/* Add Widget Button */}
                            <button
                              id={`add-widget-${widgetType}`}
                              type="button"
                              className="flex p-2 disabled:bg-gray-200 enabled:bg-accent-blue-pale rounded-full group enabled:hover:bg-accent-blue-dark text-accent-blue-dark enabled:hover:text-white"
                              onClick={() => handleAddWidget(widgetType)}
                              disabled={
                                isLoadingUpdateDashboard ||
                                isLoadingUpdateDashboard_thisComponent ||
                                isFetchingDashboardList
                              }
                            >
                              <span className="text-sm font-normal px-1">
                                {isLoadingUpdateDashboard ||
                                isLoadingUpdateDashboard_thisComponent ||
                                isFetchingDashboardList ? (
                                  <img
                                    src={svgIcons.LoadingAnimation}
                                    className="size-5"
                                    alt=""
                                  />
                                ) : (
                                  <>{t(`add`)}</>
                                )}
                              </span>
                            </button>
                          </span>
                        </h4>
                        <label
                          className="pb-6 relative cursor-pointer group"
                          htmlFor={`add-widget-${widgetType}`}
                        >
                          <span className="absolute w-full h-full flex items-center justify-center z-10 rounded-lg group-hover:bg-black/10">
                            <span className="text-lg text-black/20 font-bold uppercase -rotate-[30deg]">
                              {t("reference_data")}
                            </span>
                          </span>
                          <img
                            src={
                              widgetScreenshots[
                                widgetType === "11"
                                  ? "enum_11_12"
                                  : widgetType === "13"
                                    ? "enum_13_14"
                                    : (`enum_${widgetType}` as keyof typeof widgetScreenshots)
                              ]
                            }
                            alt={tWidgetTitles(`enum_${widgetType}`)}
                            className="w-full opacity-70"
                          />
                        </label>
                      </React.Fragment>
                    ))
                  )}
                </div>
              </AccordianControlled>
            );
          })}
        </div>

        {/* Custom Widgets - Heading */}
        <div className="text-lg mt-6 font-bold text-gray-500 mb-2">
          {t("section_custom")}
        </div>

        {/* Select Report Accordian (for creation of custom widgets) */}
        <div>
          <AccordianControlled
            title={t("custom.select_report")}
            isOpen={openAccordian === ADD_CUSTOM_WIDGETS.USE}
            buttonSpacingClass=""
            hasDetailsPage={false}
            onClick={() => {
              setOpenAccordian(
                openAccordian === ADD_CUSTOM_WIDGETS.USE
                  ? -1
                  : ADD_CUSTOM_WIDGETS.USE
              );
            }}
          >
            {/* Select Report - My Custom Reports */}
            <AccordianWithOptions
              border={false}
              title={t("custom.my_report")}
              isOpen={openReport === Number(REPORT_TYPES.CUSTOM)}
              setIsOpen={() => {
                setOpenReport(
                  openReport === Number(REPORT_TYPES.CUSTOM)
                    ? -1
                    : Number(REPORT_TYPES.CUSTOM)
                );
              }}
              titleSize="sm"
              collapsible={true}
              showButton
              buttonDimensionClass="ps-5 pe-0 py-2"
              buttonLeadingClass="leading-6"
            >
              <div className={`flex flex-col pt-0 pb-6`}>
                {dataReportsByCategoryCustom?.results
                  // .filter((report) => !!report.is_group_by_aggregate)
                  .map((subReport, subIndex) => {
                    return (
                      <WidgetSelectCustomReportButton
                        subReport={subReport}
                        onClick={() => onReportClick(subReport)}
                        key={subIndex}
                      />
                    );
                  })}
                {
                  // .filter((report) => !!report.is_group_by_aggregate)
                  dataReportsByCategoryCustom?.results.length === 0 && (
                    <p className="text-sm text-heading-gray888 opacity-70 text-center py-4">
                      {t("no_reports")}
                    </p>
                  )
                }
              </div>
            </AccordianWithOptions>

            {/* Select Report - Shared With Me */}
            <AccordianWithOptions
              border={false}
              title={t("custom.shared")}
              isOpen={openReport === Number(REPORT_TYPES.SHARED_WITH_ME)}
              titleSize="sm"
              setIsOpen={() => {
                setOpenReport(
                  openReport === Number(REPORT_TYPES.SHARED_WITH_ME)
                    ? -1
                    : Number(REPORT_TYPES.SHARED_WITH_ME)
                );
              }}
              collapsible={true}
              showButton
              buttonDimensionClass="ps-5 pe-0 py-2"
              buttonLeadingClass="leading-6"
            >
              <div className={`flex flex-col pt-0 pb-6`}>
                {dataReportsByCategoryShared?.results
                  // .filter((report) => !!report.is_group_by_aggregate)
                  .map((subReport, subIndex) => {
                    return (
                      <WidgetSelectCustomReportButton
                        subReport={subReport}
                        onClick={() => onReportClick(subReport)}
                        key={subIndex}
                      />
                    );
                  })}
                {
                  // .filter((report) => !!report.is_group_by_aggregate)
                  dataReportsByCategoryShared?.results.length === 0 && (
                    <p className="text-sm text-heading-gray888 opacity-70 text-center py-4">
                      {t("no_reports")}
                    </p>
                  )
                }
              </div>
            </AccordianWithOptions>
          </AccordianControlled>
          {/* )} */}

          {/* Create Custom Report Button */}
          <AdminFormFieldSubmit
            customWrapperClass="mt-2"
            type="button"
            variant="primary-like"
            label={
              <span className="flex justify-center whitespace-nowrap mb">
                Create Custom Report
                <img
                  className="ml-2"
                  src={svgIcons.ButtonIconPlus}
                  alt={"add / remove widgets"}
                />
              </span>
            }
            onClick={onCreateCustomReportClick}
          />
        </div>

        {/* Published Custom Widgets List (with add button) */}
        <AccordianControlled
          title={t("custom.custom_widgets")}
          hasDetailsPage={false}
          buttonSpacingClass=""
          isOpen={openAccordian === ADD_CUSTOM_WIDGETS.LIST}
          onClick={() => {
            setOpenAccordian(
              openAccordian === ADD_CUSTOM_WIDGETS.LIST
                ? -1
                : ADD_CUSTOM_WIDGETS.LIST
            );
          }}
          wrapperClass="mt-4"
          contentClass="flex-grow relative"
          handleClass="bg-accent-blue-paleO66 ps-4 pe-2 rounded-lg"
          key={6}
        >
          {availableCustomWidgets?.map((widget, index) => (
            <React.Fragment key={index}>
              <div className="flex py-4 justify-between items-center">
                <div className="text-sm font-bold mt-2 leading-4 line-clamp-3">
                  {widget.title}
                </div>
                <div className="flex gap-2">
                  {/* Add Custom Widget To Dashboard */}
                  <button
                    id={`add-widget-${widget.dashboard_type}`}
                    type="button"
                    className="flex p-2 disabled:bg-gray-200 enabled:bg-accent-blue-pale rounded-full group enabled:hover:bg-accent-blue-dark text-accent-blue-dark enabled:hover:text-white"
                    onClick={() => handleAddCustomWidget(widget)}
                    disabled={
                      isLoadingUpdateDashboard ||
                      isLoadingUpdateDashboard_thisComponent ||
                      isFetchingDashboardList ||
                      isFetchingCustomList
                    }
                  >
                    <span className="text-sm font-normal px-1">
                      {isLoadingUpdateDashboard ||
                      isLoadingUpdateDashboard_thisComponent ||
                      isFetchingDashboardList ||
                      isFetchingCustomList ? (
                        <img
                          src={svgIcons.LoadingAnimation}
                          className="size-5"
                          alt=""
                        />
                      ) : (
                        <>{t(`add`)}</>
                      )}
                    </span>
                  </button>
                  {/* Delete Custom Widget Button */}
                  {/* needs to be implemented */}
                  <button
                    type="button"
                    className="group flex items-center min-w-9 max-w-9 px-0 py-2 enabled:bg-field-error-dark/20 disabled:bg-gray-300 rounded-full enabled:hover:bg-field-error-dark enabled:text-field-error-dark disabled:text-heading-gray888 enabled:hover:text-white"
                    onClick={() =>
                      deleteCustomWidgetButtonClickCallback(widget)
                    }
                    disabled={
                      isLoadingUpdateDashboard ||
                      isLoadingUpdateDashboard_thisComponent ||
                      isFetchingDashboardList ||
                      isFetchingCustomList
                    }
                  >
                    <span className="text-sm font-normal px-2 flex justify-center">
                      <img
                        src={svgIcons.CrossRedIcon}
                        alt="cross"
                        className={`size-3 mx-2 group-hover:brightness-0 group-hover:invert group-focus:brightness-0 group-focus:invert group-disabled:grayscale group-disabled:opacity-50`}
                      />
                    </span>
                  </button>
                </div>
              </div>
              <span className="relative">
                <span className="absolute w-full h-full flex items-center justify-center z-10 rounded-lg group-hover:bg-black/10">
                  <span className="text-lg text-black/20 font-bold uppercase -rotate-[30deg]">
                    {t("reference_data")}
                  </span>
                </span>
                <img
                  src={getWidgetImage(
                    getChartTypeNameById(widget.graph_type) ??
                      EWidgetChartTypes.DOUGHNUT
                  )}
                  className="w-full opacity-70"
                />
              </span>
            </React.Fragment>
          ))}
          {/* <div className="flex py-4 justify-between items-center">
            <div className="text-sm mt-2 leading-4 line-clamp-3">
              {tWidgetTitles("safety_events")}
            </div>
            <div>
              <button
                type="button"
                className="text-xs flex p-1 bg-accent-blue-pale rounded-full group enabled:hover:bg-accent-blue-dark enabled:hover:text-white"
                // onClick={() => setShowDetails(true)}
              >
                <span className="hidden xl:block text-xs font-normal px-1">
                  Add
                </span>
                <img
                  src={svgIcons.MagnifierIcon}
                  alt="print"
                  className="xl:hidden size-5 group-enabled:group-hover:grayscale-0 group-enabled:group-hover:brightness-0 group-enabled:group-hover:invert"
                />
              </button>
            </div>
          </div> */}
          {/* <img
            src={getWidgetImage(EWidgetChartTypes.DOUGHNUT)}
            className="w-full"
          />
          <img
            src={getWidgetImage(EWidgetChartTypes.VERTICAL_BAR_GRAPH)}
            className="w-full"
          /> */}
          {/* <div
            className={`${customList?.length === 0 ? "" : "absolute"} w-full h-full flex flex-col gap-4 py-4 overflow-y-auto`}
          >
            {customList?.length === 0 ? (
              <p className="flex-grow flex items-center text-heading-gray888 opacity-70 text-sm px-10 py-4 text-center">
                {t("no_widgets")}
              </p>
            ) : (
              customList?.map((widget, index) => (
                <div className="flex py-4 justify-between items-center">
                  <div className="text-sm mt-2 leading-4 line-clamp-3">
                    {tWidgetTitles("safety_events")}
                  </div>
                  <div>
                    <button
                      type="button"
                      className="text-xs flex p-1 bg-accent-blue-pale rounded-full group enabled:hover:bg-accent-blue-dark enabled:hover:text-white"
                      // onClick={() => setShowDetails(true)}
                    >
                      <span className="hidden xl:block text-xs font-normal px-1">
                        Add
                      </span>
                      <img
                        src={svgIcons.MagnifierIcon}
                        alt="print"
                        className="xl:hidden size-5 group-enabled:group-hover:grayscale-0 group-enabled:group-hover:brightness-0 group-enabled:group-hover:invert"
                      />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div> */}
        </AccordianControlled>
      </div>
    </>
  );
};
export default ModalDashboardAddWidget;
